import React from "react";

import {
  Group,
  GroupHeader,
} from "@editor/components/common/designSystem/Group";
import Input from "@editor/components/common/designSystem/Input";
import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { nextJSProjectRouter } from "@editor/trpc/nextJSProject";

import { useNextDevServer } from "@/features/code-execution/context";
import { NextDevServerStatus } from "@/features/code-execution/useNextDevServerHook";
import Editor from "@monaco-editor/react";
import { MenuItem } from "@replo/design-system/components/menu/MenuItem";
import Switch from "@replo/design-system/components/switch/Switch";
import { ToggleGroup } from "@replo/design-system/components/toggle/ToggleGroup";
import { skipToken, useQuery } from "@tanstack/react-query";
import { File, Search } from "lucide-react";
import { useSearchParams } from "react-router-dom";

type FileTreeEntry = {
  file?: {
    contents: string;
  };
  directory?: Record<string, FileTreeEntry>;
};

type FileTreeNodeProps = {
  name: string;
  entry: FileTreeEntry;
  path: string;
  selectedFilePath: string | null;
  onFileSelect: (path: string) => void;
};

const FileTreeNode: React.FC<FileTreeNodeProps> = ({
  name,
  entry,
  path,
  selectedFilePath,
  onFileSelect,
}) => {
  if (entry.file) {
    return (
      <MenuItem
        variant="default"
        startEnhancer={<File size={16} className="text-muted" />}
        selected={selectedFilePath === path}
        onClick={() => onFileSelect(path)}
        size="sm"
        layoutClassName="ml-2"
      >
        {name}
      </MenuItem>
    );
  }

  if (entry.directory) {
    const directoryEntries = Object.entries(entry.directory).sort(([a], [b]) =>
      a.localeCompare(b),
    );

    if (directoryEntries.length === 0) {
      return (
        <Group
          name={name}
          isCollapsible={true}
          isDefaultOpen={false}
          header={<GroupHeader className="ml-2" />}
          contentClassName="ml-4"
        >
          <div className="text-sm text-muted italic py-1 px-2">
            Empty directory
          </div>
        </Group>
      );
    }

    return (
      <Group
        name={`/${name} (${directoryEntries.length})`}
        isCollapsible
        isDefaultOpen={path.split("/").length <= 2} // Auto-expand first level
        header={<GroupHeader className="ml-2" />}
        className="p-2"
        contentClassName="ml-4"
      >
        {directoryEntries.map(([childName, childEntry]) => (
          <FileTreeNode
            key={childName}
            name={childName}
            entry={childEntry}
            path={`${path}/${childName}`}
            selectedFilePath={selectedFilePath}
            onFileSelect={onFileSelect}
          />
        ))}
      </Group>
    );
  }

  return null;
};

const filterFileTree = (
  tree: Record<string, FileTreeEntry>,
  searchTerm: string,
  basePath: string = "",
): Record<string, FileTreeEntry> => {
  if (!searchTerm.trim()) {
    return tree;
  }

  const filtered: Record<string, FileTreeEntry> = {};
  const lowerSearchTerm = searchTerm.toLowerCase();

  for (const [name, entry] of Object.entries(tree).sort(([a], [b]) =>
    a.localeCompare(b),
  )) {
    const fullPath = basePath ? `${basePath}/${name}` : name;

    if (entry.file) {
      // It's a file - include if path matches search term
      if (fullPath.toLowerCase().includes(lowerSearchTerm)) {
        filtered[name] = entry;
      }
    } else if (entry.directory) {
      // It's a directory - recursively filter and include if has children
      const filteredChildren = filterFileTree(
        entry.directory,
        searchTerm,
        fullPath,
      );

      if (Object.keys(filteredChildren).length > 0) {
        filtered[name] = {
          directory: filteredChildren,
        };
      }
    }
  }

  return filtered;
};

const removeSourceAttributes = (content: string): string => {
  // Remove replosource={...} patterns where {...} can contain nested braces
  return content.replace(/\s*replosource={[^{}]*(?:{[^{}]*}[^{}]*)*}/g, "");
};

export const FileTreeExplorer = () => {
  const { exportFileTree, status } = useNextDevServer();
  const state = status >= NextDevServerStatus.Ready ? "loaded" : "not-loaded";

  const [exportDir, setExportDir] = React.useState<"full" | "workdir">(
    "workdir",
  );

  // Note (Noah, 2025-07-23): Fancy way to load an export of files from the WebContainer
  // via a custom query function so that the current export is only available to this component
  // and never gets cached.
  const {
    data: fileTree,
    isPending,
    error,
  } = useQuery({
    queryKey: ["web-container-file-tree", exportDir, state],
    queryFn: () => {
      if (!exportFileTree) {
        return null;
      }
      return exportFileTree(exportDir);
    },
    staleTime: 0,
    refetchOnMount: true,
    enabled: Boolean(exportFileTree),
  });

  const [searchParams] = useSearchParams();
  const projectId = useCurrentProjectId();
  const pageId = searchParams.get("pageId");

  const { data: page } = nextJSProjectRouter.getPage.useQuery(
    projectId ? { projectId, id: pageId ?? "" } : skipToken,
    {
      enabled: Boolean(projectId && pageId),
    },
  );

  React.useEffect(() => {
    if (error) {
      console.error("Error loading webcontainer file tree", error);
    }
  }, [error]);

  const [selectedFilePath, setSelectedFilePath] = React.useState<string | null>(
    null,
  );
  const [filePathSearchTerm, setFilePathSearchTerm] =
    React.useState<string>("");
  const [removeSourceAttrs, setRemoveSourceAttrs] =
    React.useState<boolean>(true);

  React.useEffect(() => {
    if (fileTree && page) {
      setSelectedFilePath(`app${page.pageRoute}/PageClientComponent.tsx`);
    }
  }, [fileTree, page]);

  const handleFileSelect = (path: string) => {
    setSelectedFilePath(path);
  };

  // Helper function to traverse the tree and find file contents by path
  const getFileContentsFromTree = (
    tree: Record<string, FileTreeEntry>,
    targetPath: string,
  ): string => {
    const pathParts = targetPath.split("/").filter((part) => part !== "");

    let current: Record<string, FileTreeEntry> | FileTreeEntry = tree;

    for (const part of pathParts) {
      if ("directory" in current && current.directory) {
        current = current.directory;
      }

      if (typeof current === "object" && part in current) {
        // @ts-expect-error - we know this is a valid key
        current = current[part as keyof typeof current]!;
      } else {
        return "";
      }
    }

    if (
      typeof current === "object" &&
      "file" in current &&
      current.file &&
      "contents" in current.file
    ) {
      return current.file.contents;
    }

    return "";
  };

  const getFileLanguage = (filePath: string): string => {
    const extension = filePath.split(".").pop()?.toLowerCase();
    switch (extension) {
      case "js":
      case "jsx":
      case "mjs":
        return "javascript";
      case "ts":
      case "tsx":
        return "typescript";
      case "html":
        return "html";
      case "css":
        return "css";
      case "json":
        return "json";
      case "md":
        return "markdown";
      case "py":
        return "python";
      case "sh":
        return "shell";
      case "yml":
      case "yaml":
        return "yaml";
      case "xml":
        return "xml";
      case "sql":
        return "sql";
      default:
        return "text";
    }
  };

  if (isPending) {
    return (
      <div className="p-4">
        <p className="text-sm text-muted">Loading webcontainer file tree...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <p className="text-sm text-muted">
          Error loading webcontainer file tree
        </p>
        <p className="text-sm text-muted">{error.message}</p>
      </div>
    );
  }

  if (!fileTree) {
    return (
      <div className="p-4">
        <p className="text-sm text-muted">No file tree available</p>
      </div>
    );
  }

  const filteredTree = filterFileTree(
    fileTree as Record<string, FileTreeEntry>,
    filePathSearchTerm,
  );

  const selectedFileContents = (() => {
    if (!selectedFilePath || !fileTree) {
      return "";
    }
    return getFileContentsFromTree(
      fileTree as Record<string, FileTreeEntry>,
      selectedFilePath,
    );
  })();

  return (
    <div className="flex h-full w-full">
      {/* Left sidebar with file tree */}
      <div className="w-80 bg-background overflow-auto pr-4 font-mono flex flex-col gap-2">
        {/* Search bar */}
        <Input
          placeholder="Search files..."
          value={filePathSearchTerm}
          onChange={(e) => setFilePathSearchTerm(e.target.value)}
          startEnhancer={<Search size={16} className="text-muted" />}
          size="sm"
        />

        {/* Export directory toggle */}
        <ToggleGroup
          size="sm"
          selectedValue={exportDir}
          onChange={(value) => setExportDir(value as "full" | "workdir")}
          options={[
            {
              value: "workdir",
              label: "Work Dir",
              tooltipContent: "Show only the working directory files",
            },
            {
              value: "full",
              label: "Full FS",
              tooltipContent:
                "Show complete file system including system files",
            },
          ]}
        />

        {/* File tree */}
        <div className="flex-1 overflow-auto py-2 -ml-2">
          {Object.keys(filteredTree).length === 0 && filePathSearchTerm ? (
            <div className="p-4 text-center">
              <p className="text-sm text-muted">No files match your search</p>
            </div>
          ) : (
            Object.entries(filteredTree)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([name, entry]) => (
                <FileTreeNode
                  key={name}
                  name={name}
                  entry={entry}
                  path={name}
                  selectedFilePath={selectedFilePath}
                  onFileSelect={handleFileSelect}
                />
              ))
          )}
        </div>
      </div>

      {/* Main content area */}
      <div className="flex-1 flex flex-col">
        {selectedFilePath ? (
          <>
            <div className="p-4 bg-surface border-b border-border">
              <div className="flex items-center justify-between">
                <p className="text-base font-mono">{selectedFilePath}</p>
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-default">
                    Remove source attributes
                  </label>
                  <Switch
                    isOn={removeSourceAttrs}
                    onChange={setRemoveSourceAttrs}
                    size="sm"
                  />
                </div>
              </div>
            </div>
            <div className="flex-1">
              <Editor
                width="100%"
                height="100%"
                language={getFileLanguage(selectedFilePath)}
                value={
                  removeSourceAttrs
                    ? removeSourceAttributes(selectedFileContents)
                    : selectedFileContents
                }
                options={{
                  readOnly: true,
                  minimap: { enabled: false },
                  scrollBeyondLastLine: false,
                  wordWrap: "on",
                }}
                theme="vs-dark"
              />
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <File size={48} className="text-muted mx-auto mb-4" />
              <p className="text-lg font-medium text-muted">
                Select a file to view its contents
              </p>
              <p className="text-sm text-muted">
                Choose a file from the tree on the left
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
